import React from "react";
import { cloneDeep, isEmpty } from "lodash";
import PropTypes from "prop-types";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import ContentWrapper from "../../Layout/ContentWrapper";
import { Button, Col, FormControl, Row } from "react-bootstrap-3";
import swal from "sweetalert";
import asSortable from "../Common/SortableHOC";
import VirtualizedTable from "./VirtualizedTable/VirtualizedTable";
import { DebounceInput } from "react-debounce-input";
import styled, { withTheme } from "styled-components";
import moment from "moment";
import { CSVLink } from "react-csv";
import qs from "qs";
import { DateRangePicker } from "react-dates";
import { format } from "date-fns";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import { CONTROLS_POSITION, ENTITY_NAME_TO_SECTION } from "../../Common/constants";

import globalMessages from "../../intl/globalMessages";
import EntityResultMessage from "../Common/EntityResultMessage";
import SortDirection from "react-virtualized/dist/commonjs/Table/SortDirection";
import { usePermissions } from "../../../../lib/permissions";
import { ForbiddenSection } from "../../../../components/authorization-verifier";
import { SECTION } from "../../../../lib/constants";
import filterPills from "../../../../lib/filterPills";
import { Accordion, Card } from "react-bootstrap";
import { StyledSpinner } from "../../../reservationDetail/styles";

const messages = defineMessages({
  ...globalMessages,
  yes: {
    id: "default.yes",
    defaultMessage: "yes",
  },
  no: {
    id: "default.no",
    defaultMessage: "no",
  },
  clearFilters: {
    id: "app.entityList.clearFilters",
    description: "Clear filters in entity",
    defaultMessage: "Clear filters",
  },
  create: {
    id: "app.entityList.create",
    description: "Create new entity link",
    defaultMessage: "Create new",
  },
  downloadExcel: {
    id: "app.entityList.downloadExcel",
    description: "Download Excel File",
    defaultMessage: "Download excel",
  },
  deleteSuccess: {
    id: "app.entityList.deleteSuccess",
    description: "Delete Success message",
    defaultMessage: "Entity successfully deleted!",
  },
  deleteError: {
    id: "app.entityList.deleteError",
    description: "Delete Error message",
    defaultMessage: "Error deleting entity",
  },
  error: {
    id: "app.error",
    description: "Error",
    defaultMessage: "Error",
  },
  confirmation: {
    id: "app.entityList.confirmation",
    description: "Confirmation Question",
    defaultMessage: "Are you sure?",
  },
  deleteConfirmationWarning: {
    id: "app.entityList.deleteConfirmationWarning",
    description: "Confirmation Warning",
    defaultMessage: "Once deleted, you will not be able to recover this entity!",
  },
  export: {
    id: "app.entityList.export",
    description: "Export filterd entity list",
    defaultMessage: "Exportar",
  },
  totalRecords: {
    id: "app.entityList.totalRecords",
    description: "Total records",
    defaultMessage: "Total records",
  },
  filter: {
    id: "app.entityList.filter",
    description: "Filter",
    defaultMessage: "Filter",
  },
});

const FILTER_TYPE = {
  BOOLEAN: "bool",
  DATERANGE: "daterange",
  TEXT: "text",
  SELECTOR: "selector",
};

function Loading() {
  return <div>Loading...</div>;
}

const StyledButton = styled(Button)`
  button {
    margin-bottom: 50px;
  }
`;

function FooterContainer({
  allowCreate,
  onClick,
  allowExport,
  exportFileName,
  exportData,
  exportHeaders,
  showRestrictionMessage,
  restrictionMessage,
  additionalControls,
  customCreateButtonMessage,
  onClearFilters,
  hasFilters,
  isDownloadingExcel,
  onDownloadExcel,
}) {
  const date = format(new Date(), "yyyyMMdd");

  return (
    <div>
      {allowCreate === false ? null : (
        <StyledButton className="btn btn-primary" onClick={event => onClick(event)}>
          {customCreateButtonMessage ? (
            <FormattedMessage {...customCreateButtonMessage.create} />
          ) : (
            <FormattedMessage {...messages.create} />
          )}
        </StyledButton>
      )}
      {(allowExport || hasFilters) && (
        <>
          {allowExport && (
            <CSVLink
              data={exportData}
              headers={exportHeaders}
              filename={`${exportFileName}_${date}.csv`}>
              <StyledButton className={`btn btn-default ${allowCreate !== false && "ml"}`}>
                <FormattedMessage {...messages.export} />
              </StyledButton>
            </CSVLink>
          )}
          {hasFilters && (
            <StyledButton
              className="btn btn-default ml"
              onClick={event => onClearFilters(event, true)}>
              <FormattedMessage {...messages.clearFilters} />
            </StyledButton>
          )}
        </>
      )}
      {onDownloadExcel && (
        <StyledButton
          className="btn btn-default ml"
          onClick={onDownloadExcel}>
          {isDownloadingExcel
            ? (<span>{" "}<StyledSpinner className="fa fa-spinner fa-spin" /></span>)
            : <FormattedMessage {...messages.downloadExcel} />
          }
        </StyledButton>
      )}

      {additionalControls}

      {showRestrictionMessage && <RestrictionMessage>{restrictionMessage}</RestrictionMessage>}
    </div>
  );
}

const VirtualizedTableContainer = styled.div`
  margin: 8px 0;
  font-size: 12px;
`;

const StyledFooterContainer = styled(FooterContainer)`
  display: flex;
`;

const RestrictionMessage = styled.div`
  margin-left: 10px;
  padding: 5px 15px;
  background-color: #fff4e7;
  font-weight: 600;
  color: #ff9125;
`;

const StyledTotalRecords = styled.div`
  margin: 10px 0 5px;
  color: #000000;
`;

const StyledEntityList = styled.div`
  overflow-x: auto;
`;

const TitleChevronContainer = styled.span`
  display: flex;
  align-items: center;
  max-width: 90vw;
  & > span:nth-child(2) {
    margin-right: 15px;
  }
  font-size: 1.25rem;
`;

const CustomCardTitle = styled(Card.Title)`
  display: inline-block;
  cursor: pointer;
`;

const Chevron = styled.span`
  margin-left: 10px;
  border: solid ${({ theme }) => theme.regularTextColor};
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
  transform: ${props => (props.toggleClosed ? "rotate(45deg)" : "rotate(-135deg)")};
`;

const HeaderHelperText = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

class EntityList extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.displaySuccessAlert = this.displaySuccessAlert.bind(this);

    this.state = {
      showRestrictionMessage: false,
      restrictionMessage: null,
      filterValues: {},
      activeKey: "",
      pills: null,
      focus: {},
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.entityResult !== false && prevProps.entityResult === false) {
      this.displaySuccessAlert();
    }
    if (this.props.entityError && this.props.entityError.operation === "delete") {
      swal.stopLoading();
      swal.close();
    }

    if (this.state.filterValues !== prevState.filterValues) {
      const filterLabels = this.props.filterFields.reduce((accum, curr) => {
        const field = typeof curr === "string" ? curr : curr[0];
        const f = this.getFields(this.props).find(f => f.fieldName === field);
        return { ...accum, [f.fieldName]: f.displayName };
      }, {});
      const filterVals = cloneDeep(this.state.filterValues);
      Object.entries(filterVals).forEach(([key, value]) => {
        const filter = this.props.filterFields.find(fieldDef => {
          const fieldName = typeof fieldDef === "string" ? fieldDef : fieldDef[0];
          return fieldName === key;
        });
        const filterType = typeof filter === "string" ? "text" : filter[1];
        if (filterType === FILTER_TYPE.SELECTOR) {
          const options = filter[2];
          const option = options.find(it => it.value === value);
          filterVals[key] = option ? option.label : undefined;
          return;
        }
        if (typeof value === "object" && value.start) {
          filterVals[key].start = filterVals[key].start.format("YYYY-MM-DD");
        }
        if (typeof value === "object" && value.end) {
          filterVals[key].end = filterVals[key].end.format("YYYY-MM-DD");
        }
      });
      this.setState({
        pills: filterPills(filterVals, filterLabels, filterValues => {
          Object.keys(filterValues).forEach(value => {
            if (typeof filterValues[value] === "object") {
              if (filterValues[value].start) {
                filterValues[value].start = moment(filterValues[value].start);
              } else {
                filterValues[value].start = "";
                this.updateFilterQueryString(`${value}_start`, "");
              }
              if (filterValues[value].end) {
                filterValues[value].end = moment(filterValues[value].end);
              } else {
                filterValues[value].end = "";
                this.updateFilterQueryString(`${value}_end`, "");
              }
            }
            if (!filterValues[value]) {
              this.updateFilterQueryString(value, "");
            }
          });
          this.setState({ filterValues });
        }),
      });
    }
  }

  componentDidMount() {
    const { filterFields, entityName } = this.props;
    if (filterFields) {
      //Load values from query string
      const urlFilter = qs.parse(window.location.search, { ignoreQueryPrefix: true });
      const clearFilters = urlFilter.clearFilters === "true";
      const localStorageFilters = JSON.parse(window.localStorage.getItem("filters") || "{}");
      const localStorageData =
        !isEmpty(localStorageFilters) && !clearFilters ? localStorageFilters[entityName] : {};
      const savedFilters = !isEmpty(urlFilter) ? urlFilter || {} : localStorageData || {};
      const filters = {};
      if (clearFilters) {
        this.handleClearFilters(null, false);
      }
      filterFields.forEach(fieldDef => {
        const field = typeof fieldDef === "string" ? fieldDef : fieldDef[0];
        const type = typeof fieldDef === "string" ? "text" : fieldDef[1];
        switch (type) {
          case FILTER_TYPE.TEXT:
            if (savedFilters[field]) {
              filters[field] = savedFilters[field];
            }
            break;
          case FILTER_TYPE.BOOLEAN:
            if (savedFilters[field]) {
              filters[field] =
                savedFilters[field] === "empty" ? undefined : savedFilters[field] === "true";
            }
            break;
          case FILTER_TYPE.DATERANGE:
            if (savedFilters[`${field}_start`] && savedFilters[`${field}_end`]) {
              filters[field] = {
                start: moment(savedFilters[`${field}_start`]),
                end: moment(savedFilters[`${field}_end`]),
              };
            }
            break;
          case FILTER_TYPE.SELECTOR:
            if (savedFilters[field]) {
              filters[field] = savedFilters[field] === "empty" ? undefined : savedFilters[field];
            }
            break;
          default:
            break;
        }
      });

      this.setState({
        filterValues: cloneDeep(filters),
      });
    }
  }

  getHeaders(fields) {
    return fields.reduce((headers, field) => {
      headers = [
        ...headers,
        {
          label: field.displayName,
          key: field.fieldName,
        },
      ];
      return headers;
    }, []);
  }

  getFields(props) {
    return props && props.fields && props.fields.list;
  }

  displaySuccessAlert() {
    swal(
      this.props.intl.formatMessage(messages.success),
      this.props.intl.formatMessage(messages.deleteSuccess),
      "success"
    );
  }

  displayErrorAlert() {
    swal(
      this.props.intl.formatMessage(messages.error),
      this.props.intl.formatMessage(messages.deleteError),
      "error"
    );
  }

  handleDelete({ event, entity, idx }) {
    swal({
      title: this.props.intl.formatMessage(messages.confirmation),
      text: this.props.intl.formatMessage(messages.deleteConfirmationWarning),
      icon: "warning",
      buttons: {
        cancel: this.props.intl.formatMessage(messages.cancel),
        confirm: {
          text: this.props.intl.formatMessage(messages.ok),
          closeModal: false,
          value: true,
        },
      },
      dangerMode: true,
    }).then(async willDelete => {
      if (willDelete) {
        const result = await this.props.onDelete({ event, entity, idx });
        if (
          result &&
          result.response &&
          (result.response.status !== 200 || result.response.status !== 201)
        ) {
          this.displayErrorAlert();
        } else {
          this.displaySuccessAlert();
        }
      }
      return false;
    });
  }

  handleToggle({ event, field, entity, idx }) {
    if (entity[field.restriction]) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.setState({
        showRestrictionMessage: true,
        restrictionMessage: field.restrictionMessage,
      });
    } else {
      this.setState({ showRestrictionMessage: false });

      delete entity[field.restriction];
      this.props.onToggle({ event, field, entity, idx });
    }
  }

  handleSort(props) {
    delete props.entity[props.field.restriction];
    delete props.swappedEntity[props.field.restriction];
    this.props.onSort(props);
  }

  updateFilter(field, value) {
    const filterValues = cloneDeep(this.state.filterValues);
    filterValues[field] = value;
    this.setState(
      {
        filterValues,
      },
      () => this.updateFilterQueryString(field, value === "" ? undefined : value)
    );
  }

  updateDateFilter(field, start, end) {
    const filterValues = cloneDeep(this.state.filterValues);
    const v = filterValues[field] || {};
    v.start = start;
    v.end = end;
    filterValues[field] = v;
    this.setState(
      {
        filterValues,
      },
      () => {
        this.updateFilterQueryString(`${field}_start`, start ? start.toISOString() : undefined);
        this.updateFilterQueryString(`${field}_end`, end ? end.toISOString() : undefined);
      }
    );
  }
  updateDateFocus(field, focusedInput) {
    const focus = { [field]: focusedInput };

    this.setState(s => ({
      focus,
    }));
  }

  updateBoolFilter(field, value) {
    const filterValues = cloneDeep(this.state.filterValues);
    filterValues[field] = value === "empty" ? undefined : value === "true";
    this.setState(
      {
        filterValues,
      },
      () => this.updateFilterQueryString(field, value === "empty" ? undefined : value)
    );
  }

  updateFilterQueryString(field, value) {
    const url = new URL(window.location.href);
    const current = qs.parse(url.search, { ignoreQueryPrefix: true });
    const localStorageFilters = window.localStorage.getItem("filters") || "{}";
    const localStorageData = JSON.parse(localStorageFilters);
    if (value) {
      current[field] = value;
      window.localStorage.setItem(
        "filters",
        JSON.stringify({
          ...localStorageData,
          [this.props.entityName]: {
            ...localStorageData[this.props.entityName],
            [field]: value,
          },
        })
      );
    } else {
      delete current[field];
      url.search = qs.stringify(current);
      window.history.pushState({}, "", url.toString());
      if (localStorageData[this.props.entityName]) {
        delete localStorageData[this.props.entityName][field];
      }
      if (current[field]) {
        delete current[field];
      }
      window.localStorage.setItem(
        "filters",
        JSON.stringify({
          ...localStorageData,
        })
      );
    }
    url.search = qs.stringify(current);
    window.history.pushState({}, "", url.toString());
  }

  handleClearFilters = (event, clearUrlFilter) => {
    event && event.preventDefault();
    if (clearUrlFilter) {
      const url = new URL(window.location.href);
      url.search = qs.stringify("");
      window.history.pushState({}, "", url.toString());
    }
    const localStorageFilters = window.localStorage.getItem("filters") || "{}";
    const localStorageData = JSON.parse(localStorageFilters);
    delete localStorageData[this.props.entityName];
    window.localStorage.setItem(
      "filters",
      JSON.stringify({
        ...localStorageData,
      })
    );
    this.setState({
      filterValues: {},
    });
  };

  handleAccordionToggle = () => {
    this.setState(prevState => ({ toggleClosed: !prevState.toggleClosed }));
  };

  render() {
    const {
      name,
      validation,
      renderers,
      nested,
      loading,
      entityName,
      customActions,
      allowCreate,
      allowDelete,
      allowEdit,
      allowClone,
      allowExport,
      exportFileName,
      currentLocale,
      maxColumnWidth,
      controlsPosition,
      additionalControls,
      horizontalScroll,
      showTotalRecords,
      additionalTotalRecords,
      headerLink,
      customCreateButtonMessage,
      showEmptyListColumnNames,
      hasFilters,
      isDownloadingExcel,
      onDownloadExcel,
    } = this.props;
    let entities =
      this.props.entities &&
      this.props.entities.length > 0 &&
      this.props.customEntityToListValuesMapper
        ? this.props.customEntityToListValuesMapper({
            entities: this.props.entities,
            currentLocale,
            intl: this.props.intl,
          })
        : this.props.entities;
    const {
      entityError,
      handleCreate,
      handleEdit,
      sorting,
      onClone,
      reorder,
      filterFields,
      headerHelperText,
    } = this.props;
    const { handleToggle, handleDelete, handleSort } = this;
    const fields = this.getFields(this.props);
    const headers = this.getHeaders(fields);
    if (entityError) {
      return (
        <ContentWrapper>
          <div className="content-heading">{name}</div>
          And unexpected error occurred
        </ContentWrapper>
      );
    }
    const filteredEntities = filterFields
      ? entities.filter(e =>
          filterFields.every(fieldDef => {
            const field = typeof fieldDef === "string" ? fieldDef : fieldDef[0];
            const type = typeof fieldDef === "string" ? "text" : fieldDef[1];
            const filterValue = this.state.filterValues[field];
            if (filterValue === undefined) return true;
            switch (type) {
              case FILTER_TYPE.TEXT:
                const entityValue = e[field] && e[field].toString();
                return (
                  filterValue === "" ||
                  (entityValue && entityValue.toLowerCase().includes(filterValue.toLowerCase()))
                );
              case FILTER_TYPE.DATERANGE:
                const start = filterValue.start;
                const end = filterValue.end;
                if (start && end) {
                  const v = moment(e[field]);
                  return v.isBetween(
                    start.startOf("day"),
                    end.endOf("day"),
                    undefined,
                    "[]"
                  );
                } else {
                  return true;
                }
              case FILTER_TYPE.BOOLEAN:
                return !!e[field] === filterValue;
              case FILTER_TYPE.SELECTOR: {
                const entityValue = e[field] && e[field].toString();
                return filterValue === "" || filterValue === "empty" || entityValue === filterValue;
              }
              default:
                break;
            }
            return true;
          })
        )
      : entities;
    return (
      <ContentWrapper unwrap={nested}>
        {nested ? (
          ""
        ) : (
          <>
            <div className="content-heading">
              {headerLink ? (
                <a style={{ color: this.props.theme.regularTextColor }} href={headerLink}>
                  {name}
                </a>
              ) : (
                name
              )}
            </div>
            { headerHelperText && (
              <HeaderHelperText>
                {headerHelperText}
              </HeaderHelperText>
            )}
          </>
        )}
        {loading ? (
          <Loading />
        ) : (
          <StyledEntityList>
            {filterFields && (
              <Accordion>
                <Card>
                  <Card.Body>
                    <Accordion.Toggle
                      as={CustomCardTitle}
                      variant="link"
                      eventKey="0"
                      onClick={this.handleAccordionToggle}>
                      <TitleChevronContainer>
                        <span>
                          <FormattedMessage {...messages.filter} />
                        </span>
                        <Chevron toggleClosed={this.state.toggleClosed} />
                        {this.state.toggleClosed && this.state.pills}
                      </TitleChevronContainer>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey="0" className="!visible">
                      <Row>
                        {filterFields.map(filterDefinition => {
                          const field =
                            typeof filterDefinition === "string"
                              ? filterDefinition
                              : filterDefinition[0];
                          const type =
                            typeof filterDefinition === "string" ? "text" : filterDefinition[1];
                          const f = fields.find(f => f.fieldName === field);
                          const options =
                            type === FILTER_TYPE.SELECTOR ? filterDefinition[2] : undefined;
                          if (type === FILTER_TYPE.SELECTOR && !options) {
                            throw new Error("Invalid selector filters. Missing options");
                          }
                          let filterValue = this.state.filterValues[field];
                          if (filterValue === undefined) {
                            switch (type) {
                              case FILTER_TYPE.TEXT:
                                filterValue = "";
                                break;
                              case FILTER_TYPE.DATERANGE:
                                filterValue = {};
                                break;
                              case FILTER_TYPE.BOOLEAN:
                                filterValue = "empty";
                                break;
                              case FILTER_TYPE.SELECTOR:
                                filterValue = "empty";
                                break;
                              default:
                                break;
                            }
                          }
                          return (
                            <Col sm={4} key={field} className="listFilter">
                              {type === FILTER_TYPE.TEXT && (
                                <DebounceInput
                                  element={FormControl}
                                  minLength={2}
                                  debounceTimeout={300}
                                  name={`filter-${field}`}
                                  type="text"
                                  value={filterValue}
                                  placeholder={f.displayName}
                                  onChange={event => this.updateFilter(field, event.target.value)}
                                />
                              )}
                              {type === FILTER_TYPE.DATERANGE && (
                                <DateRangePicker
                                  startDate={filterValue.start}
                                  startDateId={`start-${field}`}
                                  startDatePlaceholderText={`${f.displayName} from`}
                                  endDatePlaceholderText={`${f.displayName} to`}
                                  endDate={filterValue.end}
                                  endDateId={`end-${field}`}
                                  focusedInput={this.state.focus[field]}
                                  isOutsideRange={() => false}
                                  showClearDates
                                  onDatesChange={({ startDate, endDate }) =>
                                    this.updateDateFilter(field, startDate, endDate)
                                  }
                                  onFocusChange={focusedInput =>
                                    this.updateDateFocus(field, focusedInput)
                                  }
                                  withPortal
                                />
                              )}
                              {type === FILTER_TYPE.BOOLEAN && (
                                <FormControl
                                  value={filterValue === "empty" ? filterValue : `${filterValue}`}
                                  componentClass="select"
                                  onChange={e => this.updateBoolFilter(field, e.target.value)}>
                                  <option value="empty">{f.displayName}</option>
                                  <option value="true">
                                    {this.props.intl.formatMessage(messages.yes)}
                                  </option>
                                  <option value="false">
                                    {this.props.intl.formatMessage(messages.no)}
                                  </option>
                                </FormControl>
                              )}
                              {type === FILTER_TYPE.SELECTOR && (
                                <FormControl
                                  value={filterValue === "empty" ? filterValue : `${filterValue}`}
                                  componentClass="select"
                                  onChange={event => this.updateFilter(field, event.target.value)}>
                                  <option value="empty">{f.displayName}</option>
                                  {options.map(option => (
                                    <option value={option.value}>{option.label}</option>
                                  ))}
                                </FormControl>
                              )}
                            </Col>
                          );
                        })}
                      </Row>
                    </Accordion.Collapse>
                  </Card.Body>
                </Card>
              </Accordion>
            )}
            <VirtualizedTableContainer>
              {this.props.success && <EntityResultMessage />}

              {controlsPosition === CONTROLS_POSITION.TOP && (
                <StyledFooterContainer
                  allowExport={allowExport}
                  exportFileName={exportFileName}
                  exportData={filteredEntities}
                  exportHeaders={headers}
                  allowCreate={!!(allowCreate && handleCreate)}
                  onClick={handleCreate}
                  showRestrictionMessage={this.state.showRestrictionMessage}
                  restrictionMessage={this.state.restrictionMessage}
                  additionalControls={additionalControls}
                  customCreateButtonMessage={customCreateButtonMessage}
                  hasFilters={hasFilters}
                  onClearFilters={this.handleClearFilters}
                  isDownloadingExcel={isDownloadingExcel}
                  onDownloadExcel={onDownloadExcel}
                />
              )}

              {showTotalRecords && (
                <StyledTotalRecords>
                  <div>
                    <FormattedMessage {...messages.totalRecords} />
                    {": "}
                    {filteredEntities.length}
                  </div>
                  {additionalTotalRecords &&
                    additionalTotalRecords({ totalRecords: filteredEntities.length })}
                </StyledTotalRecords>
              )}

              {!entities.length && showEmptyListColumnNames === false ? null : (
                <VirtualizedTable
                  fields={fields}
                  entityName={entityName}
                  entities={filteredEntities}
                  validation={validation}
                  renderers={renderers}
                  onToggle={allowEdit && handleToggle}
                  onDelete={handleDelete}
                  onSort={handleSort}
                  onReorder={this.props.onReorder}
                  onEdit={handleEdit}
                  onClone={onClone}
                  edit={nested}
                  customActions={customActions}
                  sortBy={sorting ? sorting.property : null}
                  sortable={sorting ? sorting.sortable : false}
                  sortDirection={
                    sorting ? sorting.sortDirection || SortDirection.ASC : SortDirection.ASC
                  }
                  allowClone={!!(allowCreate && allowClone && onClone)}
                  reorder={!!(allowEdit && reorder && this.props.onReorder)}
                  allowEdit={!!(allowEdit && handleEdit)}
                  allowDelete={!!(allowDelete && handleDelete)}
                  maxColumnWidth={maxColumnWidth}
                  horizontalScroll={horizontalScroll}
                />
              )}
            </VirtualizedTableContainer>
          </StyledEntityList>
        )}

        {(!controlsPosition || controlsPosition === CONTROLS_POSITION.BOTTOM) && (
          <StyledFooterContainer
            allowExport={allowExport}
            exportFileName={exportFileName}
            exportData={filteredEntities}
            exportHeaders={headers}
            allowCreate={allowCreate}
            onClick={handleCreate}
            showRestrictionMessage={this.state.showRestrictionMessage}
            restrictionMessage={this.state.restrictionMessage}
            additionalControls={additionalControls}
            customCreateButtonMessage={customCreateButtonMessage}
            hasFilters={hasFilters}
            onClearFilters={this.handleClearFilters}
            isDownloadingExcel={isDownloadingExcel}
            onDownloadExcel={onDownloadExcel}
          />
        )}
      </ContentWrapper>
    );
  }
}

EntityList.propTypes = {
  name: PropTypes.string,
  entities: PropTypes.array,
  validation: PropTypes.object,
  renderers: PropTypes.object,
  fields: PropTypes.object,
  onDelete: PropTypes.func,
  onToggle: PropTypes.func,
  onSort: PropTypes.func,
  customActions: PropTypes.array,
  reorder: PropTypes.bool,
  horizontalScroll: PropTypes.bool,
};

EntityList.defaultProps = {
  horizontalScroll: false,
};

const EntityListWrapper = props => {
  const permissions = usePermissions();
  const section = ENTITY_NAME_TO_SECTION[props.entityName];
  const sectionPermissions = permissions.find(
    sectionPermissions => sectionPermissions.section === section
  );
  // TODO: remove this when C2T is configured in Keycloak
  const userProfileEmail = props.userProfileEmail;
  const isC2T =
    userProfileEmail &&
    (userProfileEmail.includes("clicktotravel.es") || userProfileEmail.includes("ayoweb.es"));
  const hasPermissions =
    ENTITY_NAME_TO_SECTION[props.entityName] !== SECTION.FILTERS ||
    (ENTITY_NAME_TO_SECTION[props.entityName] === SECTION.FILTERS && (isC2T || props.isWitAdmin));
  // END TODO
  if ((!sectionPermissions || !sectionPermissions.show) && !hasPermissions) {
    return <ForbiddenSection showMessage={props.showForbiddenMessage} />;
  }
  const actions = {
    allowCreate: sectionPermissions.create,
    allowEdit: sectionPermissions.modify,
    allowDelete: sectionPermissions.delete,
    allowImport: sectionPermissions.import,
    allowExport: sectionPermissions.export,
  };
  return <EntityList {...props} {...actions} />;
};

export default asSortable(injectIntl(withTheme(EntityListWrapper)));
